import React from 'react';

export interface FloatingToolbarAction {
  id: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'muted' | 'accent';
  icon?: React.ReactNode;
  title?: string;
}

interface FloatingToolbarProps {
  actions: FloatingToolbarAction[];
  /** Optional control after the action row (e.g. Super Admin caution). */
  trailing?: React.ReactNode;
}

const variantClass = {
  primary: 'floating-toolbar-btn-primary',
  secondary: 'floating-toolbar-btn-secondary',
  muted: 'floating-toolbar-btn-muted',
  accent: 'floating-toolbar-btn bg-orange-600/20 hover:bg-orange-600/35 border-orange-500/30',
};

/**
 * Compact bottom action bar — icons by default; labels expand on hover (pointer devices).
 */
export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ actions, trailing }) => {
  if (actions.length === 0 && !trailing) return null;

  return (
    <div className="floating-toolbar" role="toolbar" aria-label="Quick actions">
      <div className="floating-toolbar-inner">
        <div className="flex flex-nowrap justify-center items-center gap-1 sm:gap-1.5">
          {actions.map((action) => {
            const tip = action.title ?? action.label;
            return (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                className={`group ${variantClass[action.variant ?? 'primary']}`}
                title={tip}
                aria-label={action.label}
              >
                <span className="shrink-0 inline-flex">{action.icon}</span>
                <span
                  aria-hidden="true"
                  className="hidden sm:inline-block max-w-0 overflow-hidden opacity-0 whitespace-nowrap
                             text-sm font-medium
                             transition-[max-width,opacity,margin] duration-200 ease-out
                             group-hover:max-w-[14rem] group-hover:opacity-100 group-hover:ml-1.5
                             group-focus-visible:max-w-[14rem] group-focus-visible:opacity-100 group-focus-visible:ml-1.5"
                >
                  {action.label}
                </span>
              </button>
            );
          })}
          {trailing}
        </div>
      </div>
    </div>
  );
};

export default FloatingToolbar;
